/* CUSTOM_SAUNA3D_BY_CLAUDE — activation, camera behaviour and controls for the 3D sauna
   model that snippets/media-gallery.liquid hoists to gallery slide 1.

   Drives the existing theme classes from the outside rather than modifying them:
   DeferredMedia (assets/main.js:269-319) is shared with every video on the site and
   ProductModel (assets/product-model.js) with any future 3D product, so neither is touched.

   Loaded only on a sauna product template that has a .glb. */
(() => {
  'use strict';

  const FLOOR_MARGIN_IN = 0.5; // a hair above, not exactly in, the ground plane
  const IN_TO_M = 0.0254;
  const POSTER_FADE_MS = 450;

  /* ── Zoom tuning ──────────────────────────────────────────────────────────────────────
     Shopify's own +/- buttons step FIELD OF VIEW by 8deg, clamped to a hardcoded [10,45]
     (model-viewer-ui.en.js, `zoom(e)`) — about four presses end to end, and it ignores our
     min-field-of-view entirely. That is the "zooms too far on each click". Both the buttons
     and the wheel are taken over here so they share one dial and one feel.

     RATE, NOT SMOOTHING. The obvious fix for "it's a little fast" is a lower
     interpolation-decay, and that was tried during the preview build and rejected: one
     number governs ALL camera motion, so it slowed every drag and rotate too — "all
     movements/pans/rotations just got really slow". Left at the component default. How far
     one notch travels is this dial's job; how smoothly it gets there is the component's.

     Detents across the whole range, measured during the preview build: ~25 at 7% per notch,
     ~11 at 15%, ~5 at 30%. The preview settled on 30%; on the storefront that reads as too
     much, so this is 12% — roughly 14 notches end to end. */
  /* Landed at 7% after bracketing it from both sides: 12% was a visible lurch per detent, 3.5%
     was too slow to get anywhere. ~23 detents across the whole range, against ~14 at 12% and
     ~46 at 3.5%. Ryan tested both ends; this is the middle, and the useful reading is the
     detent count rather than the percentage — that is what "can I land on the view I want,
     without it taking all day" actually measures. */
  const ZOOM_PER_NOTCH = 0.13;
  const WHEEL_CAP = 130; // one detent's worth, whatever the device claims
  const WALL_MARGIN_IN = 2; // clearance kept outside the carcass, so the near plane never clips it
  const ZOOM_RATE = Math.log(1 + ZOOM_PER_NOTCH) / 100;

  /* Framing. model-viewer's `auto` radius leaves the cabin filling only ~55% of the frame
     height, which reads small beside the product photographs it now leads. Expressed as the
     share of the frame we want it to fill rather than as a multiplier of `auto`, so it holds
     for a 3-person cabin and a 6-person one alike — the Andermatt is 20" shallower than the
     Engelberg and `auto` frames the two at different distances. */
  /* ⚠ THE LOAD FRAMING IS A MARGIN, NOT A SIZE. It used to be "the cabin fills 72% of the
     frame height", computed from getDimensions().y — and a height share cannot express what is
     actually wanted, because it says nothing about where the silhouette LANDS. The Engelberg
     measured a comfortable 72% and still had its roof cap out of frame at the top and its
     front-bottom edge an inch off the bottom: the cabin is not centred on the target, the near
     corners project larger than the far ones, and at MODEL_YAW the corner that sweeps widest is
     not the one the AABB describes. None of that is visible to a height ratio.

     So it is now solved the same way the dimension overlay solves its own pull-back: project
     the eight corners of the cabin and find the closest radius that keeps every one of them
     this far inside every edge. It holds for any sauna, any yaw and any aspect without a
     per-model number — which is the point, with ~70 SKUs to come.

     5% of the frame's shorter side, ~27px on the 539px desktop slide. Started at 13% (~3/4" at
     96 CSS px to the inch) and came down three times after Ryan looked at it — the number that reads
     "too small" is not the tightest margin but the WIDEST one, and this framing is markedly
     asymmetric: at 13% the binding edge was the bottom at 12.8% while the right-hand side sat
     at 26%, because the camera is 14deg off square and the cabin is deeper than it is wide, so
     the silhouette is not centred on the target it orbits. The honest fix for that is to centre
     the silhouette and then solve, which is a camera-target shift and a bigger change than this
     needs. */
  const LOAD_MARGIN = 0.05;
  /* The corners come from the MANUAL's carcass, which the mesh oversails — the roof cap stands
     proud and the handle sticks out (deliberate licence, see CLAUDE.md). Half an inch per half
     extent covers it: measured against the world AABB at 45deg yaw, the mesh is ~1.1" larger
     than the spec across (width + depth), so ~0.55" per axis. Rounded up, and the vertical
     extent is taken from the mesh directly since yaw does not touch Y. */
  const MESH_OVERSAIL_IN = 1.0;

  /* ── THE MODEL LOADS 5% LARGER ON A NARROW STAGE ──────────────────────────────────────
     Ryan: "they're just a fraction too small, and on an already small screen, I'm okay with
     them being a little bigger."

     Expressed as a scale on the SOLVED RADIUS rather than as a smaller LOAD_MARGIN, because
     "5% bigger" is only meaningful as a size. Margin and size are not linearly related — the
     solve bisects for the closest distance keeping all eight projected corners inside the
     margin, and how much a given margin change moves the camera depends on the aspect, the
     field of view and which corner happens to bind. A margin tweak would have meant a
     different amount on each model and each screen; a radius scale means exactly 5%, always.

     ⚠ GATED ON WIDTH ALONE, not on the house touch rule. This is about how much ROOM the
     stage has, not about what is pointing at it: Ryan's laptop is a touchscreen and should
     keep desktop framing, and a phone turned sideways has 844px of width and does not need
     the help either. `(max-width: 768px)` answers the question actually being asked.

     The floor still applies after it — see the two call sites — so this can never push the
     camera inside the carcass on some future model with unusual proportions. */
  const NARROW_LOAD_ZOOM = 1.05;

  /* The most the dimension overlay may pull the camera back on a narrow stage, as a multiple
     of the load framing. See the note at the call site: a ceiling on the solve, not a better
     solve. */
  const DIM_MAX_PULLBACK = 1.4;
  const narrowStage = () => window.matchMedia('(max-width: 768px)').matches;
  const ZOOM_IN_LIMIT = 0.5; // x home radius — keeps the camera outside the carcass
  const ZOOM_OUT_LIMIT = 2.4; // x home radius

  /* Front-wall reveal. The clip is baked into the .glb (src/assemble.js), so it travels with
     the file and every sauna inherits it with no per-model work. */
  const FADE_DELAY_MS = 260;
  const FADE_MS = 520;

  /* ── The environment ──────────────────────────────────────────────────────────────────
     IMAGE-BASED LIGHTING IS A VIEWER SETTING, NOT A glTF PROPERTY. It cannot travel inside the
     .glb — the same reason the build's own studio rig does not survive export — so if this file
     does not set it, the model is lit by whatever model-viewer defaults to.

     It did not set it, and that is the whole story behind "the lighting looks different on the
     live site". model-viewer's default is a single large, hard-edged source essentially on the
     camera axis. View a sauna square-on — which is exactly what someone does when they want to
     look THROUGH the glass and see the inside — and that source lands in the glass as one broad
     rectangular blowout. The thing the customer is trying to see through stops being see-through.

     `custom-sauna3d-env.hdr` is the rig the model was built against: a key high and to the
     front-LEFT, a cooler fill low on the opposite side, a rim behind, and a deliberately broad,
     weak on-axis fill that lifts the front elevation without planting a hotspot on it. Off-axis
     and irregular, so a square-on view reflects structure rather than a wall of light.

     HDR, NOT the 4 KB PNG beside it. In LDR everything clips at 1.0 and the sky sits at 0.90,
     so a source can be at most 1.11x its surroundings however bright it is asked to be — glass
     then gets a faint sheen instead of a highlight, which is the entire failure being fixed
     here. 256x128 keeps the peak at ~30x sky for 128 KB; resolution only softens the reflector
     edges, it does not touch the ratio.

     `skybox-image` is deliberately NOT set — that would paint the map as the visible background
     instead of just lighting the model. */
  /* ── MOVING THE LIGHT WITHOUT REPLACING THE ENVIRONMENT ────────────────────────────────
     model-viewer's built-in environment puts a large soft source essentially on the camera
     axis. Square-on — which is exactly where somebody stops to look THROUGH the door and see
     inside the cabin — that source lands in the pane as a broad blowout and the glass stops
     being see-through. Ryan: "the big light can be blinding due to the reflections so you can't
     see inside the sauna anymore".

     A built-in environment cannot be edited, and REPLACING it was tried three times and failed
     on cost every time: the map cannot travel inside the .glb, so it becomes a second file the
     product page fetches and decodes, measured at 1.8x the load and a visibly brighter model.

     But the light does not have to move — the MODEL can. `orientation` turns the model inside a
     world-fixed environment, so yawing the cabin and adding the same yaw to the camera leaves
     the composition identical while the light arrives at a completely different angle. It is
     one attribute, no new asset, no download, and it keeps the environment Ryan already likes.

     30 DEGREES, measured on the storefront's own v1.12 at the square-on view, sampling the door
     pane:

       yaw  0 (before)   16.0% of the pane blown past 140
       yaw 15             9.0%
       yaw 25             6.1%
       yaw 35             4.6%
       yaw 45             1.1%      <- effectively clean
       yaw 60             0.0%

     ⚠ IT IS THE THIRD VALUE. `orientation` is ROLL PITCH YAW, so `0deg 45deg 0deg` sets PITCH
     and tips the cabin over. The first sweep run here did exactly that and produced a
     plausible-looking curve with a "clean window" at 25-35 — of a sauna lying at an angle. The
     tell was getDimensions() reporting the model 2.54m tall against its real 1.90m, which the
     storefront harness surfaced as a lost width label. A yaw leaves the height untouched.

     45 rather than 60: 1.1% is already below the point where anything reads as glare, and 45 is
     where the world footprint goes square (2.38 x 2.38), so the diagonal that sweeps the frame
     stops changing with further rotation. */
  const MODEL_YAW = 45;
  const HOME_AZ = MODEL_YAW + 14; // the hero framing is 14deg off square; keep that offset

  const ENV_URL = (() => {
    const s = document.querySelector('script[data-sauna3d-env]');
    return (s && s.dataset.sauna3dEnv) || '';
  })();

  /* ── THE TOUCH BUILD ───────────────────────────────────────────────────────────────────
     A phone loads a DIFFERENT .glb from a desktop. The only difference between them is that
     the mobile one's glass drops `transmission`.

     WHY. Transmission makes three render every opaque object a SECOND time into a separate
     lower-resolution buffer for the pane to sample. On mobile that buffer's depth precision
     is not the main pass's, so interior fittings sitting a fraction of an inch in front of
     another surface trade places with it per pixel per frame. Ryan found six on a real phone
     — backrest cleats, control panel, bench skirts, the stool's middle support, the ceiling
     lamp, the speakers — and the common factor was not any of the fittings: every one of them
     is seen THROUGH the glass, and nothing on the outside of the cabin was ever affected.

     WHY NOT ONE FILE. Alpha blending is not a substitute on desktop. The GPU multiplies a
     transparent material's whole output by opacity, SPECULAR INCLUDED, so at the 5-20% a clear
     pane needs the environment reflection is multiplied down to nothing — Ryan, on the
     single-file attempt: "no lights reflecting off the glass at all". specularFactor caps at
     1.0 in glTF, so the ~20x needed to compensate does not exist. Transmission IS the feature
     that decouples see-through from specular.

     So: desktop keeps real glass with Fresnel, refraction and the roller-wave sweep; mobile
     trades it for correctness and keeps what specular it can at 20% opacity.

     ⚠ EMPTY IS A VALID STATE. No metafield means no mobile build was published, and the phone
     loads the ordinary model — right for any SKU published before this existed, and the reason
     this is a fallback rather than a hard swap. */
  const MOBILE_SRC = (() => {
    const s = document.querySelector('script[data-sauna3d-mobile-src]');
    return (s && s.dataset.sauna3dMobileSrc) || '';
  })();

  /* The house rule, and it is a house rule for a reason: Ryan's laptop is a touchscreen, so a
     bare `pointer: coarse` test reports coarse at his desk and would hand HIM the mobile build.
     Pair it with width. (See assets/custom-header-haptics.js:15.) */
  const isTouchLayout = () =>
    window.matchMedia('(max-width: 768px) and (pointer: coarse)').matches;

  /* 1.25, NOT the preview's 1.8. 1.8 is what web/preview.html was tuned at, and taking it
     across measured mean luma 190 against 160 for no environment at all — Ryan: "the appearance
     looks way more washed out", and he was right. The preview is a dark page with one model on
     it; the storefront sits the same model on white beside product photography, and the same
     exposure reads very differently there. Tuned to land next to the look the default
     environment gives, so the environment changes the REFLECTIONS without changing the level. */
  const ENV_EXPOSURE = '1.25';

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  const SVG_NS = 'http://www.w3.org/2000/svg';

  /* A diagnostics surface for tools/storefront_check.mjs in the sauna-3d repo. This feature
     is entirely camera behaviour, so almost none of it is visible in the page source, and the
     only honest way to verify it is to drive the real page and read numbers back out. Costs
     one small object and no work. */
  const diag = (window.__sauna3d = window.__sauna3d || { wheel: 0, zoomCalls: 0 });

  /* ── Bounding-box centre, without getBoundingBoxCenter() ──────────────────────────────
     Shopify serves model-viewer v1.12, whose public API is getCameraOrbit / getCameraTarget /
     getDimensions / getFieldOfView and NOTHING ELSE. `getBoundingBoxCenter()` — which the
     internal preview uses freely, because it runs a newer build from npm — does not exist
     here, and calling it throws inside the load handler and takes the dimension overlay down
     with it.

     With `camera-target` left at auto, getCameraTarget() IS the bounding box centre. Measured
     on the Engelberg: target {0, 0.9484, 0.0183} against getDimensions().y / 2 = 0.9515, and
     that 0.0183 m z-offset is the ~0.7" the proud door handle pushes the mesh centre forward
     — the very offset the preview documents. Captured once, on load, before any interaction
     could move it, and this viewer has no pan. */
  function boundingCenter(mv) {
    if (!mv.__sauna3dCenter) {
      const t = mv.getCameraTarget();
      if (t) mv.__sauna3dCenter = { x: t.x, y: t.y, z: t.z };
    }
    return mv.__sauna3dCenter;
  }

  // ── Camera ─────────────────────────────────────────────────────────────────────────────
  // Attributes on <model-viewer>, not properties of the .glb, so they do not travel with the
  // file and have to be applied here. Framing matches the hero camera that tools/sweep.mjs
  // matched to vendor photo 1.
  function applyCameraSettings(mv) {
    /* Shopify's media_tag ships poster="…/preview_images/<sku>_1726x.jpg" — its own
       auto-generated render of the glb from model-viewer's DEFAULT camera, front on and
       slightly top down. It sits UNDER our poster panel and outlives it, so the reveal
       uncovered Shopify's render first and the real model a beat later. Our panel already
       covers the whole load, so this one has no job left. */
    mv.removeAttribute('poster');

    /* ⚠ THE ZOOM FELT SLOW BECAUSE OF THIS, NOT BECAUSE OF THE STEP SIZE. The step went 12% ->
       3.5% -> 7% -> 10% -> 13% across four rounds of feedback and "still slow" came back every
       time, which is the tell that the dial being turned is the wrong one: a wheel notch sets a
       GOAL and the component eases toward it, so past a point a bigger step is a longer ease
       rather than a faster response.

       The note by ZOOM_PER_NOTCH says smoothing was tried during the preview build and
       rejected — but that experiment RAISED the decay and made everything sluggish, which is
       the opposite end of the same dial. Lowering it was never tried. 30ms, against the
       component's default of 50.

       It governs ALL camera motion, deliberately: a drag lands sooner too, which is the same
       complaint one input earlier. It remains an ease, so the dimension overlay's glide — the
       one movement here that is meant to be seen as a movement — survives it. */
    mv.setAttribute('interpolation-decay', '30');

    /* ── THE "DRAG ME" WIGGLE STARTS SOONER ───────────────────────────────────────────────
       Ryan: "I don't want to run the risk that someone lands on mobile, and scrolls down the
       page too fast to see the first oscillation and therefore doesn't realize it's an
       interactive 3D model they can touch and rotate."

       model-viewer's default threshold is 3000ms and it is counted from the model becoming
       loaded and visible — NOT from when our poster clears. That is the part that made it
       later than it looks: the panel covers the load, so by the time the customer can see the
       sauna at all a chunk of those three seconds is already spent, and on a phone the whole
       gallery may have scrolled past before the first swing.

       200ms, applied on BOTH platforms — Ryan said he did not mind, and a desktop visitor
       skimming past has the same problem with a smaller consequence.

       ⚠ THE ATTRIBUTE DOES NOT BUY AS MUCH AS IT LOOKS LIKE IT SHOULD, and the number is set
       where it is because of that. Measured on a phone viewport, time from `loaded` to the
       camera actually moving:

           threshold 3000 (the default)   ->  ~6.3s
           threshold  200                 ->  ~4.2s

       So it is honoured roughly one-for-one, on top of a FLOOR of about four seconds that the
       threshold does not reach — model-viewer's own prompt ramps in rather than starting at
       full swing, and nothing exposed controls that. Dropping below ~200 buys nothing; the
       remaining delay is not ours. Shortening it further would mean driving the swing
       ourselves, which is rebuilding a component feature to save a second and is not worth it
       unless Ryan says the floor is still too slow.

       ⚠ FIRING WHILE THE POSTER IS STILL UP IS NOT WASTED. The prompt repeats until the
       customer interacts, so an early trigger means the model is ALREADY oscillating at the
       moment the panel fades — which is strictly better than starting from still. That is why
       this is a plain threshold and not something rearmed off `data-sauna3d-fading`: no
       coordination is needed and there is no timer to starve.

       Still dismissed by hand on the first wheel notch — see the interaction-prompt note in
       the zoom takeover, which explains why the component cannot see that gesture itself. */
    mv.setAttribute('interaction-prompt-threshold', '200');
    // Guarded, so a missing or renamed asset falls back to model-viewer's default rather than
    // setting environment-image="" and rendering the model black.
    /* ⚠ THE ENVIRONMENT IS NOT SET HERE. It is applied in onReady, after the model has loaded.
       Setting `environment-image` on this build BEFORE the glb has loaded renders the cabin's
       exterior solid black — measured on the storefront's v1.12 at 31% near-black pixels
       against 0% for every other configuration, with the map fetching 200 and no console error.
       The identical file applied after load is not merely fine but brighter than no environment
       at all (mean luma 170 against 160). It is an ordering bug in v1.12, not a bad map, and
       every "fix the file" theory — resolution, RLE encoding — measured identical because none
       of them was the variable. See applyEnvironment(). */
    mv.setAttribute('shadow-intensity', '1.05');
    mv.setAttribute('shadow-softness', '0.55');
    // roll PITCH yaw — the yaw is the THIRD value. See the note on MODEL_YAW.
    mv.setAttribute('orientation', `0deg 0deg ${MODEL_YAW}deg`);
    mv.setAttribute('camera-orbit', `${HOME_AZ}deg 86deg auto`);
    mv.setAttribute('min-field-of-view', '8deg');
    mv.setAttribute('max-field-of-view', '45deg');
    // Placeholders only. The real radius limits are not knowable until the model has loaded
    // and `state.home` is solved; onReady narrows both to the same numbers zoomBy() enforces.
    mv.setAttribute('min-camera-orbit', 'auto auto 0.1m');
    mv.setAttribute('max-camera-orbit', 'auto 89deg 40m');

    /* camera-orbit is a GOAL the component glides toward, and its starting point is
       model-viewer's own default — 0deg 75deg, i.e. front on and looking slightly down. So the
       first frames after the model becomes renderable are a swing from that default across to
       14deg 86deg, and any of them that escape from under the poster read as "the model
       appeared at the wrong angle, then corrected". Land it before anything can be drawn.
       Radius stays `auto` here; only onReady can solve the real one. */
    if (typeof mv.jumpCameraToGoal === 'function') mv.jumpCameraToGoal();
  }

  /* ── The environment, applied AFTER the model is loaded ────────────────────────────────
     Ordering is the whole trick here (see applyCameraSettings). Calls back once the new
     lighting is actually on screen, so the reveal can wait for it rather than uncovering the
     model under the default environment and re-lighting it in front of the customer.

     `environment-change` is the signal; the timeout is a backstop, because a signal that never
     fires would otherwise hold the poster up forever — a failure far worse than the wrong
     lighting for a moment. */
  function applyEnvironment(mv, done) {
    if (!ENV_URL) return done();
    /* ⚠ THE REVEAL DOES NOT WAIT FOR THIS. It used to — the reveal was held until
       `environment-change` fired, so the customer would never see the cabin lit by the default
       and re-lit a beat later. That was correct in principle and a disaster in practice: it put
       the environment fetch, its decode and model-viewer's PMREM generation all in front of the
       first frame, and Ryan measured the load at five times what it had been. A visible
       re-light lasting a few frames is a far smaller cost than five times the wait, and the
       poster is still covering for most of it.

       Applied on the LOAD event rather than at element creation, which is the ordering v1.12
       needs — see applyCameraSettings. */
    mv.setAttribute('environment-image', ENV_URL);
    mv.setAttribute('exposure', ENV_EXPOSURE);
    done();
  }

  /* ── The radius limits, applied to the ELEMENT rather than only inside zoomBy ────────────
     ZOOM_IN_LIMIT and ZOOM_OUT_LIMIT used to be enforced in zoomBy() alone — which the wheel
     and the hijacked +/- buttons both call, so on desktop the dial was bounded and the limits
     looked like they worked.

     THEY DID NOT APPLY ON TOUCH. A phone has no wheel, and Shopify hides its own +/- on
     non-desktop (`.shopify-model-viewer-ui:not(.--desktop) .shopify-model-viewer-ui__controls-area
     { display: none }`, specificity (0,3,0) against our (0,2,1) — their decision, and correct:
     pinch is the native gesture). So the ONLY zoom on a phone is pinch, pinch is handled inside
     model-viewer, and it never passes through zoomBy(). It was bounded solely by the 0.1m
     placeholder above. Measured on the Engelberg: one hard two-finger spread took the camera to
     0.60m against a 1.60m limit, and the cabin's own half-diagonal is 1.20m — i.e. the customer
     ends up standing inside the walls looking at their backfaces.

     Expressed as attributes, the clamp is gesture-agnostic: wheel, buttons and pinch all obey
     it, and zoomBy's own clamp becomes a belt-and-braces duplicate rather than the only guard. */
  function applyRadiusLimits(mv, state) {
    if (!state.home) return;
    mv.minCameraOrbit = `auto auto ${state.home * ZOOM_IN_LIMIT}m`;
    mv.maxCameraOrbit = `auto 89deg ${state.home * ZOOM_OUT_LIMIT}m`;
  }

  // ── Floor clamp ────────────────────────────────────────────────────────────────────────
  // Without this a customer can orbit under the unit, where the ground shadow vanishes and
  // they are looking up through a floor that is not there.
  //
  // A fixed max phi CANNOT express "above the floor" — the crossing angle depends on radius,
  // and the constant that was tried (89deg, borrowed from Weight Designer) stranded the
  // camera 37" up on a 74.9" sauna. It has to be solved per frame.
  //
  // Written ONLY while dragging. Assigning maxCameraOrbit re-applies the controls' options
  // and cancels a camera glide in flight; radius is an input here, so during a wheel zoom
  // every notch moved the limit and each rewrite killed the interpolation the previous notch
  // started — the zoom froze partway. A drag is the only gesture that can put the camera
  // under the floor, and radius is constant through one, so gating on drag removes the
  // interference rather than trading it off.
  function attachFloorClamp(mv, state) {
    let dragging = false;
    let lastMaxPhi = null;

    const clampFloor = () => {
      if (!dragging) return;
      const d = mv.getDimensions();
      const c = boundingCenter(mv);
      const t = mv.getCameraTarget();
      const o = mv.getCameraOrbit();
      if (!d || !c || !o || !o.radius) return;

      const floorY = c.y - d.y / 2 + FLOOR_MARGIN_IN * IN_TO_M;
      // acos of a value clamped to [-1,1]: when the camera is closer to the target than the
      // target is high, no angle puts it underground and the limit simply opens up.
      const phi = (Math.acos(clamp((floorY - t.y) / o.radius, -1, 1)) * 180) / Math.PI;
      if (lastMaxPhi !== null && Math.abs(phi - lastMaxPhi) < 0.25) return;
      lastMaxPhi = phi;
      // Carry the solved radius limit through. This used to write a bare `40m`, which silently
      // undid applyRadiusLimits() on the first drag — and a drag is exactly what precedes a
      // pinch, so the zoom clamp would have been reinstated and then thrown away again on
      // every gesture. Falls back to 40m only while home is still unsolved.
      const far = state && state.home ? state.home * ZOOM_OUT_LIMIT : 40;
      mv.maxCameraOrbit = `auto ${phi.toFixed(2)}deg ${far}m`;
    };

    mv.addEventListener('pointerdown', () => {
      dragging = true;
      clampFloor();
    });
    window.addEventListener('pointerup', () => { dragging = false; });
    window.addEventListener('pointercancel', () => { dragging = false; });
    mv.addEventListener('camera-change', clampFloor);
  }

  /* ── Model space <-> world space ────────────────────────────────────────────────────────
     The model carries MODEL_YAW so the storefront's light stops hitting the glass square on.
     It is a pure rotation about Y through the origin — the cabin is built centred on the origin
     with its floor at y=0 — so the same pair converts points AND directions, with no
     translation term. */
  const YAW_R = (MODEL_YAW * Math.PI) / 180;
  const YAW_C = Math.cos(YAW_R);
  const YAW_S = Math.sin(YAW_R);
  const modelToWorld = (p) =>
    MODEL_YAW ? { x: p.x * YAW_C + p.z * YAW_S, y: p.y, z: -p.x * YAW_S + p.z * YAW_C } : p;
  const worldToModel = (p) =>
    MODEL_YAW ? { x: p.x * YAW_C - p.z * YAW_S, y: p.y, z: p.x * YAW_S + p.z * YAW_C } : p;

  /** Unit vector from target to camera for model-viewer's (theta, phi). */
  const orbitDir = (theta, phi) => ({
    x: Math.sin(phi) * Math.sin(theta),
    y: Math.cos(phi),
    z: Math.sin(phi) * Math.cos(theta),
  });

  /* ── The wall guard ─────────────────────────────────────────────────────────────────────
     THE CAMERA MUST NOT GET INSIDE THE CABIN. Not a rendering concern — an honesty one. The
     exterior is the surface this project has verified; the inside of a wall is where the
     omissions, the un-mitred returns and the seams never meant to be seen from six inches away
     live.

     ⚠ ZOOM_IN_LIMIT ALONE STOPPED BEING SUFFICIENT the moment zoom-to-cursor could move the
     target. That limit is a multiple of the home radius and assumes the target is at the
     cabin's centre; anchor the target on a wall and half the home radius puts the camera
     straight through it. This is a ray/box exit test instead — the smallest radius along the
     current orbit direction that still leaves the carcass — which holds however far the target
     has wandered, which is exactly the case that matters.

     Done in MODEL space. The box is the cabin's own; the world AABB is the yawed envelope and
     measures 2.38m across a 1.57m-wide sauna, which would hold the camera a foot and a half
     further out than the wall it is guarding. */
  function wallRadius(state, T, dir) {
    const box = state.box;
    if (!box) return 0;
    const t = worldToModel(T);
    const d = worldToModel(dir);
    let tNear = -Infinity;
    let tFar = Infinity;
    for (const a of ['x', 'y', 'z']) {
      if (Math.abs(d[a]) < 1e-9) {
        // Parallel to this slab: either always inside it or never.
        if (t[a] < box.lo[a] || t[a] > box.hi[a]) return 0;
        continue;
      }
      const t1 = (box.lo[a] - t[a]) / d[a];
      const t2 = (box.hi[a] - t[a]) / d[a];
      tNear = Math.max(tNear, Math.min(t1, t2));
      tFar = Math.min(tFar, Math.max(t1, t2));
    }
    if (tFar < Math.max(tNear, 0)) return 0; // the ray misses the cabin: no constraint
    return tFar + WALL_MARGIN_IN * IN_TO_M;
  }

  /* ── What the cursor is pointing at, in world space, on the OUTSIDE of the cabin ─────────
     `positionAndNormalFromPoint` looks like the obvious tool and is the wrong one, twice over,
     and both were learned the hard way in the preview build:

       1. IT RETURNS MODEL SPACE, while getCameraTarget() is world. Feeding one into an anchor
          built from the other mixes two frames and the zoom lands somewhere unrelated to the
          pointer — which is exactly what "it doesn't go where my mouse is" looks like.
       2. IT SEES THROUGH THE GLASS. The first mesh a ray meets through the door is a bench, not
          the door, so the aim point landed INSIDE the cabin.

     So the ray is built from the camera basis and intersected with the cabin's own box, taking
     the ENTRY point — the outside of the shell by construction. */
  function aimPoint(mv, state, px, py, C, T, rect) {
    if (!state.box) return null;
    const f = { x: T.x - C.x, y: T.y - C.y, z: T.z - C.z };
    const fl = Math.hypot(f.x, f.y, f.z) || 1;
    f.x /= fl;
    f.y /= fl;
    f.z /= fl;
    /* right = forward x world-up. GET THE ORDER RIGHT: written the other way round this is the
       LEFT vector, which negates both lateral axes and point-mirrors the aim about the centre of
       the frame — "when the mouse is high and I zoom in, it takes me low". Measured in the
       preview before the fix: cursor high moved the target 2.7" DOWN. */
    const rt = { x: -f.z, y: 0, z: f.x };
    const rl = Math.hypot(rt.x, rt.z) || 1;
    rt.x /= rl;
    rt.z /= rl;
    const up = {
      x: rt.y * f.z - rt.z * f.y,
      y: rt.z * f.x - rt.x * f.z,
      z: rt.x * f.y - rt.y * f.x,
    };

    const tanV = Math.tan(((mv.getFieldOfView() || 45) * Math.PI) / 360);
    const tanH = tanV * (rect.width / rect.height);
    const ndcX = (px / rect.width) * 2 - 1;
    const ndcY = 1 - (py / rect.height) * 2;
    const vWorld = {
      x: f.x + rt.x * ndcX * tanH + up.x * ndcY * tanV,
      y: f.y + rt.y * ndcX * tanH + up.y * ndcY * tanV,
      z: f.z + rt.z * ndcX * tanH + up.z * ndcY * tanV,
    };

    const o = worldToModel(C);
    const v = worldToModel(vWorld);
    const box = state.box;
    let tN = -Infinity;
    let tF = Infinity;
    for (const ax of ['x', 'y', 'z']) {
      if (Math.abs(v[ax]) < 1e-9) {
        if (o[ax] < box.lo[ax] || o[ax] > box.hi[ax]) return null;
        continue;
      }
      const t1 = (box.lo[ax] - o[ax]) / v[ax];
      const t2 = (box.hi[ax] - o[ax]) / v[ax];
      tN = Math.max(tN, Math.min(t1, t2));
      tF = Math.min(tF, Math.max(t1, t2));
    }
    if (tF < Math.max(tN, 0)) return null; // the pointer is off the cabin entirely
    const k = Math.max(tN, 0);
    return modelToWorld({ x: o.x + v.x * k, y: o.y + v.y * k, z: o.z + v.z * k });
  }

  // ── Zoom ───────────────────────────────────────────────────────────────────────────────
  function attachZoom(mv, state) {
    /* ── ZOOM TO CURSOR ───────────────────────────────────────────────────────────────────
       model-viewer zooms along the orbit radius toward a FIXED target, so scrolling always
       pulls you toward the centre of the cabin whatever you were looking at. To inspect a hinge
       you had to zoom, drag, zoom, drag. This was built and tuned in web/preview.html; it is
       the same relation, with three things the preview version could not do here.

       THE MATHS. Camera C, target T, radius r, and P the point under the cursor. Scaling the
       radius by s while setting T' = P + (T - P)·s gives C' - P = s·(C - P): the camera slides
       straight down the line toward P with its direction unchanged, so P holds exactly still on
       screen.

       APPLIED PER TICK, NOT AS A FRACTION OF THE JOURNEY. The preview's first version derived
       the target from how far in you were, so the FIRST notch aimed at dead centre and the
       cursor only took over near the end — "it'll zoom in on dead center, then at a certain
       level the camera angles towards the mouse". The relation above is applied on its own each
       tick, so the point under the cursor is anchored from the first notch.

       ZOOMING OUT RECENTRES, and it is the same relation inverted: anchored on the load
       framing's own centre with the offset scaled by r_before/r_after, the target converges back
       as the radius grows. Zoom into a corner, scroll back out, land square on the cabin again —
       no special case, no animation.

       ⚠ WHAT THE PREVIEW DOES THAT DOES NOT PORT:
         * it calls `getBoundingBoxCenter()`, which DOES NOT EXIST on the storefront's v1.12.
           The centre comes from boundingCenter(), captured at load before anything pans it.
         * it intersects the ray with the WORLD AABB, which is fine at yaw 0 and wrong here: at
           MODEL_YAW that box is the rotated envelope, 2.38m across a 1.57m cabin, so the aim
           point would land in mid-air well in front of the wall. Both ray tests run in model
           space instead.
         * its target never moves at load. Ours is panned by centreAndFit, so "out" recentres on
           state.homeTarget rather than on the bounding-box centre — otherwise every zoom-out
           would quietly undo the load framing. */
    const zoomBy = (factor) => {
      const o = mv.getCameraOrbit();
      if (!o || !state.home) return;
      diag.zoomCalls++;
      const T = mv.getCameraTarget();
      const dir = orbitDir(o.theta, o.phi);
      const inner = Math.max(state.home * ZOOM_IN_LIMIT, wallRadius(state, T, dir));
      const next = clamp(o.radius * factor, inner, state.home * ZOOM_OUT_LIMIT);
      diag.lastZoom = { factor, from: o.radius, to: next, home: state.home };
      if (Math.abs(next - o.radius) < 1e-6) return;
      /* The +/- buttons have no cursor to aim at, so they keep the centred behaviour — but
         zooming OUT still recentres, exactly as the wheel does, or the two controls would
         disagree about where "out" ends up. */
      const home = state.homeTarget || boundingCenter(mv);
      if (T && home && next > o.radius) {
        const s = next / o.radius;
        mv.cameraTarget =
          `${home.x + (T.x - home.x) / s}m ` +
          `${home.y + (T.y - home.y) / s}m ` +
          `${home.z + (T.z - home.z) / s}m`;
      }
      mv.cameraOrbit = `${(o.theta * 180) / Math.PI}deg ${(o.phi * 180) / Math.PI}deg ${next}m`;
    };
    state.zoomBy = zoomBy;

    // Capture phase on the PARENT. At the target, listeners fire in the order they were
    // added regardless of capture, and model-viewer's own wheel handler was added first —
    // so listening on the element itself would not get there in time.
    const host = mv.parentElement || mv;
    host.addEventListener(
      'wheel',
      (e) => {
        diag.wheel++;
        if (!state.home) return; // still framing — let the component have it
        e.preventDefault();
        e.stopPropagation();

        /* ⚠ THE INTERACTION PROMPT HAS TO BE DISMISSED BY HAND, and the reason is the two lines
           above. model-viewer retires its own "drag me" wiggle when it sees the customer
           interact — but this handler runs in the capture phase and kills the wheel event
           before the component ever gets it, which is the whole point of the takeover. So the
           component still believed nobody had touched anything: a customer could zoom right in
           and the sauna would keep twisting itself back and forth with a mouse glyph over it,
           demonstrating a gesture they had just finished using.

           Set once, on the first notch. `none` rather than stopping it mid-swing, so it also
           cannot come back later during the same visit. Dragging needs no equivalent: pointer
           events are left alone and the component sees those for itself. */
        if (!mv.__sauna3dPrompted) {
          mv.__sauna3dPrompted = true;
          mv.setAttribute('interaction-prompt', 'none');
        }

        /* Everything is measured from where the camera ACTUALLY is, not from a goal we keep.
           The component glides toward a goal, so while a scroll is in progress the goal is
           somewhere the camera is not; anchoring on it compounds and the point under the
           pointer slides away. Reading live makes each notch exact for the motion it causes,
           and scrolling faster than the glide self-limits — the better failure. */
        const o = mv.getCameraOrbit();
        const T = mv.getCameraTarget();
        if (!o || !T) return;

        // deltaMode 1 is lines, 2 is pages. A trackpad reports pixels and a mouse wheel
        // often reports lines; without this a wheel zooms ~16x faster than a trackpad.
        const raw =
          e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * 100 : e.deltaY;
        const dy = Math.sign(raw) * Math.min(Math.abs(raw), WHEEL_CAP);

        const dir = orbitDir(o.theta, o.phi);
        const rBefore = o.radius;
        const inner = Math.max(state.home * ZOOM_IN_LIMIT, wallRadius(state, T, dir));
        const asked = Math.exp(dy * ZOOM_RATE);
        const rAfter = clamp(rBefore * asked, inner, state.home * ZOOM_OUT_LIMIT);
        const pinned = Math.abs(rAfter - rBefore) < 1e-6;

        /* ⚠ THE RECENTRE HAS TO OUTLIVE THE RADIUS CLAMP. The offset shrinks by the radius
           ratio, and the whole zoom-out range is only ZOOM_OUT_LIMIT (2.4x) — so a zoom-in that
           anchored 6.4" off centre came back to 2.67" and stopped there, pinned at the far
           limit with the cabin still visibly off to one side. Measured exactly: 6.4 / 2.4.
           Scrolling further did nothing at all, because the radius could not move and the early
           return went with it.
           So when the radius is pinned, the target still converges — by the ratio that WAS
           asked for rather than the one granted. Keep scrolling out and you always end up
           square on the cabin, which is what "zoom out puts you back" has to mean. */
        /* Pinned at the far limit the radius contributes nothing, so the target would crawl
           home at 13% a scroll — 6.5" off centre took twelve notches to reach 1.12", which
           still reads as "it did not recentre". Once the radius has nothing left to give, the
           convergence is deliberately steeper so a couple more scrolls finish it. Only ever on
           the pinned tail: while the radius is still moving, the exact inverse relation is what
           makes zoom-out the mirror of zoom-in. */
        const s = pinned ? Math.pow(asked, 4) : rAfter / rBefore;
        if (pinned && dy < 0) return; // at a wall: nothing left to give in either sense

        let t = T;
        if (dy < 0) {
          const rect = mv.getBoundingClientRect();
          const C = {
            x: T.x + dir.x * rBefore,
            y: T.y + dir.y * rBefore,
            z: T.z + dir.z * rBefore,
          };
          const P = aimPoint(mv, state, e.clientX - rect.left, e.clientY - rect.top, C, T, rect);
          // No hit means the cursor is over empty background — hold the target rather than
          // snapping, so scrolling past the edge of the model does not fling the view.
          if (P) {
            t = {
              x: P.x + (T.x - P.x) * s,
              y: P.y + (T.y - P.y) * s,
              z: P.z + (T.z - P.z) * s,
            };
          }
        } else {
          const home = state.homeTarget || boundingCenter(mv);
          if (home) {
            const off = Math.hypot(T.x - home.x, T.y - home.y, T.z - home.z);
            // Converging on a point is asymptotic; land on it rather than crawling at it
            // forever. 3mm is a tenth of an inch on a 5-foot cabin.
            t =
              off < 0.003
                ? home
                : {
                    x: home.x + (T.x - home.x) / s,
                    y: home.y + (T.y - home.y) / s,
                    z: home.z + (T.z - home.z) / s,
                  };
            if (pinned && off < 0.003) return; // fully out and fully centred: nothing to do
          } else if (pinned) {
            return;
          }
        }

        diag.zoomCalls++;
        diag.lastZoom = { factor: s, from: rBefore, to: rAfter, home: state.home, cursor: dy < 0 };
        mv.cameraTarget = `${t.x}m ${t.y}m ${t.z}m`;
        mv.cameraOrbit = `${o.theta}rad ${o.phi}rad ${rAfter}m`;
      },
      { passive: false, capture: true }
    );
  }

  // ── Front wall reveal ──────────────────────────────────────────────────────────────────
  // `timeScale` sets the direction and the mixer is allowed to run: +1 opens, -1 closes, and
  // the pose HOLDS at whichever end it reaches. Three approaches were tried and failed during
  // the preview build and are recorded so they are not retried: `currentTime` is not usable
  // as a scrub bar in this build (writing 1 leaves it reading 0 and the model unmoved), a
  // PAUSED action does not pose at all, and an action left to finish on its own gets reset
  // because `clampWhenFinished` is not exposed.
  function makeReveal(mv) {
    let armed = false;
    let baseColors = null;
    let fadeFrame = null;
    let fadeCur = 1;

    const frontMats = () =>
      ((mv.model && mv.model.materials) || []).filter(
        (m) => m.name && m.name.indexOf('frontWall') === 0
      );

    const setFade = (a) => {
      const mats = frontMats();
      if (!mats.length) return;
      if (!baseColors) {
        baseColors = new Map();
        for (const m of mats) {
          baseColors.set(m.name, m.pbrMetallicRoughness.baseColorFactor.slice());
        }
      }
      for (const m of mats) {
        const c = baseColors.get(m.name) || [1, 1, 1, 1];
        // Back to OPAQUE when fully home: a material left on BLEND is skipped by the
        // transmission pass, so the wall would go missing when seen through the door glass.
        m.setAlphaMode(a > 0.995 ? 'OPAQUE' : 'BLEND');
        m.pbrMetallicRoughness.setBaseColorFactor([c[0], c[1], c[2], c[3] * a]);
      }
      fadeCur = a;
    };

    // Starts a beat AFTER the wall does, so it reads as leaving rather than dissolving on the
    // spot, and finishes before the clip's last fifth — where the node collapses to nothing
    // and takes its ground shadow with it.
    const tweenFade = (to, delay) => {
      const from = fadeCur;
      const t0 = performance.now() + (delay || 0);
      if (fadeFrame != null) cancelAnimationFrame(fadeFrame);
      const step = () => {
        const u = clamp((performance.now() - t0) / FADE_MS, 0, 1);
        const e = u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
        setFade(from + (to - from) * e);
        fadeFrame = u < 1 ? requestAnimationFrame(step) : null;
      };
      step();
    };

    const arm = () => {
      if (armed) return true;
      if (!mv.availableAnimations || mv.availableAnimations.indexOf('reveal') === -1) return false;
      mv.animationName = 'reveal';
      armed = true;
      return true;
    };

    mv.addEventListener('load', () => {
      armed = false;
      baseColors = null;
      fadeCur = 1;
      arm();
    });

    return {
      available: () => Boolean(mv.availableAnimations && mv.availableAnimations.indexOf('reveal') !== -1),
      play(open) {
        if (!arm()) return;
        mv.timeScale = open ? 1 : -1;
        mv.play({ repetitions: 1 });
        tweenFade(open ? 0 : 1, open ? FADE_DELAY_MS : 0);
      },
    };
  }

  // ── The camera, exactly as model-viewer builds it ─────────────────────────────────────
  // theta is measured from +Z toward +X, phi down from +Y. `radius` is overridable so fit()
  // can ask "where would these points land at 4.3m?" without moving anything.
  function makeProjector(W, H, o, t, fovDeg, radius) {
    const r = radius == null ? o.radius : radius;
    const sp = Math.sin(o.phi);
    const eye = {
      x: t.x + r * sp * Math.sin(o.theta),
      y: t.y + r * Math.cos(o.phi),
      z: t.z + r * sp * Math.cos(o.theta),
    };
    let fx = t.x - eye.x;
    let fy = t.y - eye.y;
    let fz = t.z - eye.z;
    const fl = Math.hypot(fx, fy, fz) || 1;
    fx /= fl;
    fy /= fl;
    fz /= fl;
    // right = forward x (0,1,0). Degenerate only looking straight down, which the polar
    // clamp does not allow — but guard rather than write NaN into the DOM.
    let rx = -fz;
    let rz = fx;
    const rl = Math.hypot(rx, rz);
    if (rl < 1e-6) return null;
    rx /= rl;
    rz /= rl;
    // up = right x forward, with right = (rx, 0, rz)
    const ux = -rz * fy;
    const uy = rz * fx - rx * fz;
    const uz = rx * fy;
    const tanHalf = Math.tan((fovDeg * Math.PI) / 360);
    const aspect = W / H;
    return {
      eye,
      W,
      H,
      project(P) {
        const dx = P.x - eye.x;
        const dy = P.y - eye.y;
        const dz = P.z - eye.z;
        const zv = dx * fx + dy * fy + dz * fz;
        if (zv <= 1e-6) return null; // behind the camera
        const xv = dx * rx + dz * rz;
        const yv = dx * ux + dy * uy + dz * uz;
        return {
          x: ((xv / (zv * tanHalf * aspect)) * 0.5 + 0.5) * W,
          y: (0.5 - (yv / (zv * tanHalf)) * 0.5) * H,
        };
      },
    };
  }

  /* ── The closest the camera can be and still leave a margin ─────────────────────────────
     Shared by the load framing and by the dimension overlay's fit, because they are the same
     question asked about different points: "how far back do these world points all sit inside
     the frame with `margin` px to spare?"

     Bisection, because projected extent shrinks monotonically as the radius grows. The test is
     PER EDGE rather than "is the box small enough": nothing here is centred in the frame, so
     something that fits by total size can still be clipped on one side.

     Answered WITHOUT MOVING THE CAMERA, which is the whole point — the old dimension fit moved
     it, measured, scaled and jumped, three times over, and that is what made turning
     dimensions on look stepped. */
  function solveRadius(points, o, t, fovDeg, W, H, margin, base) {
    const fits = (r) => {
      const pr = makeProjector(W, H, o, t, fovDeg, r);
      if (!pr) return true;
      for (const P of points) {
        const p = pr.project(P);
        if (!p) return false;
        if (p.x < margin || p.x > W - margin || p.y < margin || p.y > H - margin) return false;
      }
      return true;
    };
    let lo = base;
    let hi = base;
    for (let i = 0; i < 12 && !fits(hi); i++) {
      lo = hi;
      hi *= 1.3;
    }
    if (!fits(hi)) return hi; // ran out of room; the caller clamps
    for (let i = 0; i < 24; i++) {
      const m = (lo + hi) / 2;
      if (fits(m)) hi = m;
      else lo = m;
    }
    return hi;
  }

  // ── Dimensions ─────────────────────────────────────────────────────────────────────────
  // The lines measure THE PRODUCT, NOT THE MESH. The mesh is knowingly ~0.9" wide and ~1.7"
  // deep of the real carcass (the roof cap oversails, the handle stands proud — deliberate
  // licence so it reads well zoomed). A dimension is the one thing here a customer acts on:
  // it is what they check their alcove against. So the figures come from the manual via the
  // sauna3d.dimensions metafield, and the lines are laid out on THAT box, drawn from the
  // assembler's origin convention (centre of the footprint, on the floor).
  //
  // Occlusion still tests against the MESH bounds, because that is what is on screen doing
  // the hiding. Measure the product, hide behind the model.
  function makeDims(mv, host, ext, state) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'sauna3d-dims');
    svg.setAttribute('aria-hidden', 'true');
    host.appendChild(svg);

    /* ── The overlay projects its own points; it does not use model-viewer hotspots ────────
       It used to: twelve 1px <div>s in `slot="hotspot-*"`, positioned by the component every
       frame, read back with getBoundingClientRect. That was replaced for three reasons, and
       the first is a bug the customer could see.

       1. HOTSPOTS DO NOT INHERIT `orientation`. Session 12 yawed the model 45deg so the
          storefront's light stopped hitting the glass square on, and turned the camera by the
          same angle to keep the composition. The model turned; the hotspots did not, because
          model-viewer applies `orientation` to the model container and mounts hotspots outside
          it. So the lines went on measuring a sauna that was no longer there, and every sauna
          showed its dimensions at 45deg to itself. Measured before the fix: the width run,
          which is a pure X segment and must read horizontal on a square-on view, fell 86px
          over 92px of screen — a 43deg diagonal.
       2. Reading twelve rects every frame forces twelve layout flushes, and the old draw()
          then rebuilt the whole overlay with innerHTML — re-parsing six <text> nodes, which is
          the expensive kind. Measured on this page: 1.42ms per frame against 0.035ms for
          moving attributes on nodes that already exist. That is the only work our code adds
          while dimensions are on, and model-viewer lowers its own render resolution when
          frames run long — which is what "the model goes blurry with dimensions on, and
          sometimes clears after 20 seconds" looks like from the outside.
       3. Projecting ourselves means the framing solve can be COMPUTED rather than measured, so
          turning dimensions on is one smooth camera move instead of three jumps. See fit().

       ⚠ The projection is not an approximation of model-viewer's — it is the same camera,
       verified point for point against the hotspots it replaced: 0.00px error on all twelve,
       at the live camera, on the storefront's own v1.12. If it ever needs re-checking, that is
       the test: put the hotspots back temporarily and diff the two. */
    const YAW = (MODEL_YAW * Math.PI) / 180;
    const YC = Math.cos(YAW);
    const YS = Math.sin(YAW);
    // model -> world. The exact inverse of the world -> model turn in camModel(); both are
    // no-ops when MODEL_YAW is 0.
    const toWorld = (p) =>
      MODEL_YAW
        ? { x: p[0] * YC + p[2] * YS, y: p[1], z: -p[0] * YS + p[2] * YC }
        : { x: p[0], y: p[1], z: p[2] };

  /* px of clearance the annotation keeps from every frame edge.

     ⚠ A FLAT 58 WAS A DESKTOP NUMBER APPLIED TO A PHONE, and it was not a small error.
     58 on each side is 116px — 21% of the 539px desktop stage, but 30% of a 390px phone. The
     solve could not fit the annotation in the 274px that left, so it ran all the way out and
     was CLAMPED at ZOOM_OUT_LIMIT. Measured in full screen on a 390x844 phone: radius 3.395 ->
     8.148, a 2.4x pull-back, producing an annotation of 183x147px sitting in ~100px of slack
     left and right and 340px of empty space above and below.

     Ryan: "On mobile the same thing happens, but it doesn't need to. The sauna can stay the
     same size, b/c there's ample white space." Exactly right, and the white space in that
     measurement is the proof.

     10px on a narrow stage. NOT zero, and not skipping the solve altogether: the fit is what
     guarantees the lines and labels are inside the frame at all, and on a phone the model is
     already 5% larger than it used to be. Keeping the solve with a small pad means the camera
     moves only as far as it genuinely must — usually barely — and can still never clip.

     Evaluated per fit rather than captured once, because full screen and rotation both cross
     the 768px line while this overlay exists. */
    const pad = () => (narrowStage() ? 10 : 58);
    const INTRO_MS = 1320; // must outlast the longest delay + duration in the CSS

    let on = false;
    let set = [];
    let nodes = [];
    let box = null;
    let frame = null;
    let animTimer = null;
    let restore = null;

    function build() {
      if (!mv.model) return;
      const lo = { x: (-ext.w / 2) * IN_TO_M, y: 0, z: (-ext.d / 2) * IN_TO_M };
      const hi = { x: (ext.w / 2) * IN_TO_M, y: ext.h * IN_TO_M, z: (ext.d / 2) * IN_TO_M };
      const o = 0.1; // metres, ~4" — a dimension drawn ON the wall it measures is unreadable

      /* ⚠ BUILT IN MODEL SPACE, from the spec's own extents — NOT from getDimensions().
         getDimensions() returns the WORLD axis-aligned box, and once the model carries a
         MODEL_YAW that box is the rotated cabin's envelope: at 45deg it measures 2.38m across
         a 1.57m-wide sauna. Testing model-space endpoints against it declared most of the runs
         buried and the width dimension disappeared entirely.
         Pulled in a hair so a line lying on a face plane cannot graze its own reference solid
         and report itself hidden. */
      box = {
        lo: { x: lo.x + 0.004, y: lo.y + 0.004, z: lo.z + 0.004 },
        hi: { x: hi.x - 0.004, y: hi.y - 0.004, z: hi.z - 0.004 },
      };

      const W = ext.w.toFixed(2) + '" W';
      const D = ext.d.toFixed(2) + '" L';
      const H = ext.h.toFixed(2) + '" H';

      set = [
        { id: 'wf', a: [lo.x, lo.y, hi.z + o], b: [hi.x, lo.y, hi.z + o], label: W },
        { id: 'wr', a: [lo.x, lo.y, lo.z - o], b: [hi.x, lo.y, lo.z - o], label: W },
        { id: 'dl', a: [lo.x - o, lo.y, lo.z], b: [lo.x - o, lo.y, hi.z], label: D },
        { id: 'dr', a: [hi.x + o, lo.y, lo.z], b: [hi.x + o, lo.y, hi.z], label: D },
        { id: 'hf', a: [lo.x - o, lo.y, hi.z + o], b: [lo.x - o, hi.y, hi.z + o], label: H },
        { id: 'hr', a: [hi.x + o, lo.y, lo.z - o], b: [hi.x + o, hi.y, lo.z - o], label: H },
      ];

      /* The nodes are made ONCE and afterwards only have their attributes moved. Rebuilding
         them per frame is not merely slower — it restarts every CSS animation on every frame,
         which is why the arrival animation could not exist while draw() used innerHTML. */
      svg.textContent = '';
      nodes = set.map((dim) => {
        const g = document.createElementNS(SVG_NS, 'g');
        const line = (cls) => {
          const el = document.createElementNS(SVG_NS, 'line');
          el.setAttribute('class', cls);
          g.appendChild(el);
          return el;
        };
        const run = line('d-run');
        const t1 = line('d-tick');
        const t2 = line('d-tick');
        const text = document.createElementNS(SVG_NS, 'text');
        text.textContent = dim.label;
        g.appendChild(text);
        svg.appendChild(g);
        return { g, run, t1, t2, text, shown: true };
      });
    }

    const view = (radius) => {
      const s = host.getBoundingClientRect();
      const o = mv.getCameraOrbit();
      const t = mv.getCameraTarget();
      if (!s.width || !s.height || !o || !t) return null;
      return makeProjector(s.width, s.height, o, t, mv.getFieldOfView(), radius);
    };

    /* Into MODEL space. The orbit is world, the occlusion box is the model's own, and the model
       is yawed by MODEL_YAW — so the camera has to be turned back through the same angle or
       every sight-line is tested against a cabin that is not where the model thinks it is. */
    const camModel = (eye) => {
      if (!MODEL_YAW) return eye;
      return { x: eye.x * YC - eye.z * YS, y: eye.y, z: eye.x * YS + eye.z * YC };
    };

    // Does the sight-line from eye to point pass through the cabin's box? Slab method — about
    // twenty operations and EXACT for a box, which this cabin is.
    //
    // NOT model-viewer's hotspot occlusion, which is a NORMAL test and says nothing about a
    // body between the line and the eye. And not a raycast: a real depth test needs the line
    // SAMPLED along its length, because its middle can be buried while both ends stick out
    // past the corners — ~100 rays a frame against ~20k triangles with no BVH.
    function occluded(cam, P) {
      if (!box) return false;
      const d = { x: P.x - cam.x, y: P.y - cam.y, z: P.z - cam.z };
      let t0 = 0;
      let t1 = 1; // the segment, not the infinite ray
      for (const ax of ['x', 'y', 'z']) {
        if (Math.abs(d[ax]) < 1e-9) {
          if (cam[ax] < box.lo[ax] || cam[ax] > box.hi[ax]) return false;
        } else {
          let ta = (box.lo[ax] - cam[ax]) / d[ax];
          let tb = (box.hi[ax] - cam[ax]) / d[ax];
          if (ta > tb) {
            const s = ta;
            ta = tb;
            tb = s;
          }
          if (ta > t0) t0 = ta;
          if (tb < t1) t1 = tb;
          if (t0 > t1) return false;
        }
      }
      return true;
    }

    const show = (n, yes) => {
      if (n.shown === yes) return;
      n.shown = yes;
      n.g.style.display = yes ? '' : 'none';
    };

    function draw() {
      frame = null;
      if (!on) return;
      const pr = view();
      if (!pr) return;
      svg.setAttribute('viewBox', '0 0 ' + pr.W + ' ' + pr.H);
      const cam = camModel(pr.eye);

      for (let i = 0; i < set.length; i++) {
        const dim = set[i];
        const n = nodes[i];
        if (!n) continue;
        // The whole line goes if any of the three samples is buried; half a dimension line
        // emerging from a wall looks like a bug rather than a partial view.
        const mid = [
          (dim.a[0] + dim.b[0]) / 2,
          (dim.a[1] + dim.b[1]) / 2,
          (dim.a[2] + dim.b[2]) / 2,
        ];
        const buried = [dim.a, mid, dim.b].some((p) =>
          occluded(cam, { x: p[0], y: p[1], z: p[2] })
        );
        if (buried) {
          show(n, false);
          continue;
        }
        const A = pr.project(toWorld(dim.a));
        const B = pr.project(toWorld(dim.b));
        if (!A || !B) {
          show(n, false);
          continue;
        }
        const dx = B.x - A.x;
        const dy = B.y - A.y;
        const len = Math.hypot(dx, dy);
        // Edge-on, the ends collapse onto each other and the tick direction is numerical
        // noise — skip rather than draw a hairball.
        if (len < 12) {
          show(n, false);
          continue;
        }
        const px = -dy / len;
        const py = dx / len;
        const T = 5;
        n.run.setAttribute('x1', A.x);
        n.run.setAttribute('y1', A.y);
        n.run.setAttribute('x2', B.x);
        n.run.setAttribute('y2', B.y);
        n.t1.setAttribute('x1', A.x - px * T);
        n.t1.setAttribute('y1', A.y - py * T);
        n.t1.setAttribute('x2', A.x + px * T);
        n.t1.setAttribute('y2', A.y + py * T);
        n.t2.setAttribute('x1', B.x - px * T);
        n.t2.setAttribute('y1', B.y - py * T);
        n.t2.setAttribute('x2', B.x + px * T);
        n.t2.setAttribute('y2', B.y + py * T);
        n.text.setAttribute('x', (A.x + B.x) / 2 + px * 11);
        n.text.setAttribute('y', (A.y + B.y) / 2 + py * 11);
        show(n, true);
      }
    }

    // camera-change fires many times per frame — coalesce to one draw.
    const queue = () => {
      if (frame == null) frame = requestAnimationFrame(draw);
    };
    mv.addEventListener('camera-change', queue);
    mv.addEventListener('load', () => {
      build();
      queue();
    });
    window.addEventListener('resize', queue);

    /* ── HOW FAR BACK THE CAMERA HAS TO GO ─────────────────────────────────────────────────
       The dimension lines are NOT inside the model's silhouette. They stand 0.1m off each face,
       and the two width runs sit at floor level on the near and far faces — the near one is
       closer to the camera than anything in the cabin, so it projects BELOW the model's base
       and straight off the bottom of the frame. Measured before any of this existed: a run from
       y=528 to y=581 in a 539-tall viewBox, i.e. mostly off-canvas. The button worked; there
       was simply nothing to see, which is indistinguishable from broken.

       This is now SOLVED rather than measured. The old version moved the camera, read the
       hotspots back, scaled the radius, jumped, and did it again — up to three instant camera
       jumps in consecutive frames, which is exactly why turning dimensions ON looked stepped
       while turning them OFF (a single glide to a known radius) looked smooth. Projecting our
       own points means a candidate radius can be TRIED WITHOUT MOVING ANYTHING, so the answer
       is known before the camera has stirred and the whole move is one glide.

       The bisection itself is solveRadius() up top, shared with the load framing — same
       question, different points. */
    const fit = (o, t, fovDeg, W, H, base) =>
      solveRadius(
        set.flatMap((dim) => [toWorld(dim.a), toWorld(dim.b)]),
        o, t, fovDeg, W, H, pad(), base
      );

    const deg = (rad) => (rad * 180) / Math.PI;

    return {
      /* Turning dimensions on keeps the customer's ANGLE and solves only the distance, then
         glides there once. Turning it off puts them back exactly where they were, field of
         view included.

         The field of view is restored as well as the radius because model-viewer's zoom drives
         BOTH: a pinched-in camera sits at ~10deg, and a solve that assumed the maximum would be
         wrong by a factor of four. It is also why the fit is computed at the HOME field of view
         rather than the current one — `fieldOfView = 'auto'` is set in the same breath, so by
         the time the glide lands, the camera is back at the wide angle the solve assumed. */
      toggle(next) {
        on = next;
        clearTimeout(animTimer);
        svg.classList.remove('is-intro', 'is-outro');
        if (on) {
          build();
          const o = mv.getCameraOrbit();
          const s = host.getBoundingClientRect();
          const t0 = mv.getCameraTarget();
          restore =
            o && t0
              ? { radius: o.radius, fov: mv.getFieldOfView(), target: { x: t0.x, y: t0.y, z: t0.z } }
              : null;

          /* THE TARGET GOES HOME FIRST, for the same reason the radius and field of view do.
             Zoom-to-cursor can leave it anchored on a door hinge, and fitting the annotation
             around a centre like that composes a view with the cabin shoved into a corner —
             "show me the dimensions" should produce the same readable result every time. Put
             back on the way out, so the customer returns to exactly the crop they were on. */
          const t = state && state.homeTarget ? state.homeTarget : t0;
          if (state && state.homeTarget) {
            mv.cameraTarget = `${t.x}m ${t.y}m ${t.z}m`;
          }

          if (o && t && s.width && s.height) {
            const fov = state && state.homeFov ? state.homeFov : mv.getFieldOfView();
            const base = state && state.home ? state.home : o.radius;
            let r = fit(o, t, fov, s.width, s.height, base);
            if (state && state.home) r = Math.min(r, state.home * ZOOM_OUT_LIMIT);

            /* ⚠ HARD CEILING ON THE PULL-BACK, NARROW STAGES ONLY.
               Ryan: "I just want the sauna to not shrink as much on mobile when you toggle the
               dims on." The solve's answer was 2.0x even after the padding was fixed, and 2.0x
               halves the sauna on the screen where it is already smallest.

               This overrides the solve rather than improving it, and that is the point: the
               solve is CORRECT — it returns the distance at which every drawn run fits, and on
               a 390px stage that genuinely costs 2x, because the dimension runs stand off the
               cabin's faces and the near width run projects well outside its silhouette. Three
               attempts at making the solve cleverer are recorded in the changelog; all three
               returned the same number, because the number was never wrong.

               So this is a deliberate trade, not a fix: the model stays legible and a run that
               does not fit is allowed to run past the frame edge. That is the right way round
               on a phone — the sauna is the product, the dimension line is an annotation about
               it, and a clipped annotation still reads while a halved sauna does not.

               1.4x. Desktop is untouched and still solves exactly: 58px of pad in a 539px
               stage costs it only 1.13x, so there is nothing to trade there. */
            if (narrowStage() && state && state.home) {
              r = Math.min(r, state.home * DIM_MAX_PULLBACK);
            }
            mv.fieldOfView = 'auto';
            // NO jumpCameraToGoal: the write is a goal, and the glide to it IS the animation.
            mv.cameraOrbit = deg(o.theta) + 'deg ' + deg(o.phi) + 'deg ' + r + 'm';
          }

          svg.classList.add('is-on');
          draw(); // immediately, so the arrival animation has geometry to run on
          svg.classList.add('is-intro');
          animTimer = setTimeout(() => svg.classList.remove('is-intro'), INTRO_MS);
        } else {
          /* The lines leave before the camera does, and quickly — a staggered exit reads as
             hesitation. `is-on` comes off at the end so the overlay's own opacity transition
             does not fight the outro. */
          svg.classList.add('is-outro');
          animTimer = setTimeout(() => {
            svg.classList.remove('is-outro');
            svg.classList.remove('is-on');
          }, 180);
          if (restore) {
            const o = mv.getCameraOrbit();
            mv.fieldOfView = restore.fov + 'deg';
            const rt = restore.target;
            mv.cameraTarget = `${rt.x}m ${rt.y}m ${rt.z}m`;
            mv.cameraOrbit = deg(o.theta) + 'deg ' + deg(o.phi) + 'deg ' + restore.radius + 'm';
            restore = null;
          }
        }
      },
    };
  }

  /* ── The loading pill ──────────────────────────────────────────────────────────────────
     Spans, not divs: `.media-poster` is a <button>, which may only contain phrasing content.
     Appended INSIDE the poster so it inherits the poster's own opacity fade and leaves with
     it — one transition, not two that have to be kept in step. */
  function addLoader(host) {
    const poster = host.querySelector('.media-poster');
    if (!poster || poster.querySelector('.sauna3d-loading')) return;
    const el = document.createElement('span');
    /* `h5` is the theme's own heading class, the one the product tab headings carry
       (`h2.disclosure__title.h5`). Borrowing it rather than restating Montserrat/700/22.6px
       means this line stays in step with the tabs if the type scale is ever retuned, and it
       already responds at every breakpoint. */
    el.className = 'sauna3d-loading h5';
    el.setAttribute('aria-hidden', 'true'); // the poster button already has an accessible name
    el.appendChild(document.createTextNode('Loading 3D Model'));
    for (let i = 0; i < 3; i++) {
      const dot = document.createElement('span');
      dot.className = 'sauna3d-loading__dot';
      dot.textContent = '.';
      el.appendChild(dot);
    }
    poster.appendChild(el);
  }

  function removeLoader(host) {
    const el = host.querySelector('.sauna3d-loading');
    if (el) el.remove();
  }

  /* ── Back to the hero framing, done OFF SCREEN ─────────────────────────────────────────
     The <model-viewer> is never torn down when the customer moves to another gallery slide —
     the carousel just scrolls away from it. So returning to it, by thumbnail, by arrow or by
     swipe, used to present whatever they had left behind: some half-orbit, a zoom level, the
     dimension overlay still up. It should present the same view a fresh load does.

     Reset on EXIT rather than on return. Every part of it is visible — the front wall swinging
     back takes ~520ms, the overlay clears, the camera moves — and doing that as the slide
     arrives is its own little loading sequence, which is the thing being removed. Done as the
     slide leaves, all of it happens where nobody is looking and the return is instant.

     An IntersectionObserver rather than the theme's own active-media events: arrows, thumbs and
     swipe are three different code paths in media-gallery.js but they all end with the slide
     scrolled out of the viewer, so this catches all three and owes nothing to theme internals. */
  /* ── Shopify PAUSES the model on every slide change, and never resumes it ──────────────
     media-gallery.js:412 — customSetActiveMedia() calls window.pauseAllMedia(this) on EVERY
     change, and main.js:260 turns that into `model.modelViewerUI.pause()` for every
     <product-model> on the page. The resume path, MediaGallery.playActiveMedia (:529), looks
     for `deferred-media` — which does NOT match a <product-model> element, even though
     ProductModel extends DeferredMedia. So a model is paused on the way past and nothing
     anywhere plays it again.

     Measured on the Engelberg, coming back to slide 1:

       camera orbit   14deg 86deg 3.19m  ->  0deg 75deg 4.19m   (model-viewer's DEFAULT)
       class          (none)             ->  shopify-model-viewer-ui__disabled
       poster button  hidden             ->  VISIBLE

     which is, in order, every symptom: the front-on top-down view instead of the hero framing,
     a model that ignores the mouse (the disabled class carries pointer-events: none), the white
     disc with the cube back on top, and the interaction-prompt wiggle restarting because
     model-viewer believes it has just been revealed.

     Neutered rather than repaired, because a pause that nothing can undo has no correct
     behaviour to preserve. pause() exists to stop AR and animation off-screen; model-viewer
     already throttles its own render loop when it is out of the viewport, so there is nothing
     left for it to save. Scoped to our own hoisted model — any other 3D product on the site
     keeps Shopify's behaviour.

     modelViewerUI is created asynchronously by Shopify.loadFeatures (product-model.js:19-27),
     so it has to be waited for. */
  function keepModelLive(host) {
    let tries = 0;
    const patch = () => {
      const ui = host.modelViewerUI;
      if (ui && typeof ui.pause === 'function') {
        if (!ui.__sauna3dNoPause) {
          ui.__sauna3dNoPause = true;
          ui.pause = function () {};
        }
        return;
      }
      if (++tries > 200) return; // ~20s, then give up quietly
      setTimeout(patch, 100);
    };
    patch();
  }

  /* The repair for a pause that already landed — pauseAllMedia can fire before the patch above
     is in place, and this is cheap enough to run on every return regardless. */
  function undoPause(mv) {
    mv.classList.remove('shopify-model-viewer-ui__disabled');
    if (!mv.hasAttribute('camera-controls')) mv.setAttribute('camera-controls', '');
    mv.removeAttribute('poster');
    if (typeof mv.dismissPoster === 'function') mv.dismissPoster();
  }

  /* ── The cursor over the model ──────────────────────────────────────────────────────────
     `grab` / `grabbing` are the semantically right keywords and the wrong ones to ship: Chrome
     has no system cursor to ask Windows for on either, so it scales a small bundled bitmap and
     the hand over the model came out visibly blurrier, and a different colour, to every other
     control on the site. `pointer` and `move` DO map to system cursors (IDC_HAND and
     IDC_SIZEALL), which the OS draws at the customer's own scaling, in the customer's own
     scheme — the same mechanism that makes Add to Cart look right.

     ⚠ A drawn open-hand/fist pair was built and shipped before this, and pulled back out. Two
     reasons, either of which is fatal on its own: a raster is only sharp at the densities you
     ship, and Windows fractional scaling (125%, 150%) resamples the nearest one straight back
     into blur; and a cursor scheme is a per-machine accessibility setting that NOTHING in CSS
     or JS can read, so any fill colour we pick is wrong for whoever runs the other one. The
     generator is gone; the reasoning is in the changelog.

     THE RULE HAS TO GO INSIDE THE SHADOW ROOT. Measured on the storefront's v1.12: the element
     under the pointer is model-viewer's internal <canvas>, and it carries its own `cursor:
     grab` from the component's shadow stylesheet. A page-level rule on the host does not reach
     it — not even with !important, because the two rules match different elements and the
     canvas's own cascaded value beats anything inherited. (The newer build in web/preview.html
     sets the cursor as an INLINE STYLE instead, which would need a completely different fix.
     Another one our own viewer could not have told us.) */
  function attachCursors(mv) {
    const CSS =
      'canvas { cursor: pointer !important; }\n' +
      ':host([data-sauna3d-dragging]) canvas { cursor: move !important; }';

    const inject = () => {
      const root = mv.shadowRoot;
      if (!root) return false;
      if (!root.querySelector('style[data-sauna3d-cursor]')) {
        const style = document.createElement('style');
        style.setAttribute('data-sauna3d-cursor', '');
        style.textContent = CSS;
        root.appendChild(style);
      }
      return true;
    };

    if (!inject()) mv.addEventListener('load', inject, { once: true });
  }

  /* ── Fade the furniture while the model is being handled ───────────────────────────────
     Marks the gallery busy for as long as the customer is actually manipulating the model, and
     for a beat afterwards. CSS does the rest.

     ONLY REAL INPUT COUNTS. `camera-change` is the obvious event and it is the wrong one: it
     also fires for the load framing, the dimension fit and the return-to-slide reset, so the
     controls would fade themselves out on page load and after every navigation — while the
     customer is doing nothing at all.

     No timer while the pointer is down. An idle timeout during a drag would bring the arrows
     back mid-gesture if someone dragged slowly for longer than it; the clock only starts when
     they let go. */
  function attachBusyFade(host, mv) {
    const gallery = host.closest('media-gallery');
    if (!gallery) return;

    /* These are only half of what the customer feels: the fade back in is a 0.35s CSS
       transition on top, so the wait after letting go is the timer PLUS 350ms. Tune them
       against that total, not against each other. Halved from 650/900 in session 13 — a
       full second before the controls came back read as sluggish.

       The wheel window stays the longer of the two because a wheel has no `pointerup`: the
       only signal that zooming has stopped is ticks having stopped arriving, so it has to
       outlast the gap between two notches or the controls flash back mid-zoom. */
    const IDLE_AFTER_DRAG = 325;
    const IDLE_AFTER_WHEEL = 450;
    let timer = null;
    let down = false;

    const arm = () => {
      gallery.setAttribute('data-sauna3d-busy', '');
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const release = (ms) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        gallery.removeAttribute('data-sauna3d-busy');
      }, ms);
    };

    /* The same gesture drives the cursor's open-hand -> fist swap, so it is tracked here
       rather than in a second pair of listeners: `data-sauna3d-dragging` is read by the rule
       attachCursors() puts in the shadow root. It goes on the model-viewer element itself so
       that rule can be `:host([data-sauna3d-dragging])`; model-viewer observes only its own
       attributes, so a data- one is inert to it. */
    mv.addEventListener('pointerdown', () => {
      down = true;
      mv.setAttribute('data-sauna3d-dragging', '');
      arm();
    });
    const up = () => {
      if (!down) return;
      down = false;
      mv.removeAttribute('data-sauna3d-dragging');
      release(IDLE_AFTER_DRAG);
    };
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);

    // Passive, and separate from the zoom handler — that one runs in the capture phase on the
    // host and calls preventDefault; this only observes.
    host.addEventListener(
      'wheel',
      () => {
        arm();
        if (!down) release(IDLE_AFTER_WHEEL);
      },
      { passive: true }
    );
  }

  function attachSlideReset(host, mv, state, controls) {
    const root = host.closest('.media-viewer') || null;
    let wasIn = true;

    const toHome = () => {
      if (!state.home) return;
      mv.fieldOfView = 'auto';
      const o = mv.getCameraOrbit();
      if (!o) return;
      /* THE TARGET HAS TO COME BACK TOO, now that zoom-to-cursor can move it. Resetting the
         orbit alone would return the customer to the right distance and angle around a centre
         they had dragged onto a door hinge — the sauna off in a corner of the frame, which is
         a stranger result than the one this reset exists to prevent. */
      if (state.homeTarget) {
        const t = state.homeTarget;
        mv.cameraTarget = `${t.x}m ${t.y}m ${t.z}m`;
      }
      mv.cameraOrbit = `${HOME_AZ}deg 86deg ${state.home}m`;
      if (typeof mv.jumpCameraToGoal === 'function') mv.jumpCameraToGoal();
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const isIn = e.intersectionRatio >= 0.5;
          if (wasIn && !isIn && state.home) {
            // LEAVING: put everything back where a fresh load would have it, off screen, so
            // the return is instant rather than a little sequence of its own.
            if (controls) controls.reset();
            toHome();
          } else if (!wasIn && isIn && state.home) {
            /* RETURNING: undo any pause that got in, then re-land the framing. The camera is
               re-set here as well as on the way out because pause() moves it AFTER we left —
               it fires as part of the change that brings this slide back. */
            undoPause(mv);
            toHome();
            requestAnimationFrame(toHome);
          }
          wasIn = isIn;
        }
      },
      { root: root, threshold: [0, 0.5, 1] }
    );
    io.observe(host);
  }

  // ── Controls ───────────────────────────────────────────────────────────────────────────
  /* Re-solve the load framing for the stage's CURRENT size, and keep the customer's zoom.
     Used when the stage changes shape under the model: entering or leaving full screen, and
     rotating the phone while in it.

     Rebuilt from `state.box` rather than re-reading getDimensions(), because that is the
     CARCASS box the load solve already agreed on — the mesh oversails it by design (roof cap,
     door handle) and re-deriving from the mesh would frame a slightly different object than
     the first solve did, so a refit would visibly nudge the model even at an unchanged size.

     ZOOM IS PRESERVED AS A RATIO, not as a radius: someone who has pushed in to look at the
     heater should still be that far in after rotating the phone, not thrown back to the load
     framing. Radius alone would mean a different amount of sauna at a different stage size. */
  function refitFraming(mv, host, state) {
    if (!state || !state.box || !state.homeTarget || !state.home) return;
    const s = host.getBoundingClientRect();
    if (!s.width || !s.height) return;
    const o = mv.getCameraOrbit();
    const d = mv.getDimensions();
    if (!o || !d) return;

    const yr = (MODEL_YAW * Math.PI) / 180;
    const yc = Math.cos(yr);
    const ys = Math.sin(yr);
    const corners = [];
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        for (const y of [state.box.lo.y, state.box.hi.y]) {
          const x = sx * state.box.hi.x;
          const z = sz * state.box.hi.z;
          corners.push({ x: x * yc + z * ys, y, z: -x * ys + z * yc });
        }
      }
    }

    /* Solved at the angle the camera is ACTUALLY at, not at HOME_AZ. The load solve can use
       the home angle because it runs before anyone has touched the model; here they may have
       turned it, and the silhouette this is fitting is the one on screen. */
    const goal = { theta: o.theta, phi: o.phi, radius: o.radius };
    const floor = Math.hypot(d.x, d.z) / 2 + 0.2;
    const margin = LOAD_MARGIN * Math.min(s.width, s.height);
    const solved = solveRadius(
      corners, goal, state.homeTarget, mv.getFieldOfView(), s.width, s.height, margin, floor
    );

    const ratio = state.home > 0 ? o.radius / state.home : 1;
    /* Same narrow-stage zoom as the load solve, re-evaluated rather than remembered: entering
       full screen or rotating the phone can cross the 768px line in either direction, and the
       framing should follow the stage it is actually on. */
    state.home = Math.max(solved / (narrowStage() ? NARROW_LOAD_ZOOM : 1), floor);
    applyRadiusLimits(mv, state);
    const next = clamp(state.home * ratio, state.home * ZOOM_IN_LIMIT, state.home * ZOOM_OUT_LIMIT);
    mv.cameraOrbit = `${o.theta}rad ${o.phi}rad ${next}m`;
  }

  /* ── EXPAND TO FULL SCREEN ────────────────────────────────────────────────────────────
     Ryan: the two control buttons crowd the model on a phone. The gallery stage measures
     350x499 on a 390px device and the control row alone needs 241px of that width.

     A FIXED OVERLAY, NOT THE FULLSCREEN API. `requestFullscreen` on a non-video element is
     unsupported on iOS Safari, so on an iPhone the button would appear and do nothing. The
     overlay also keeps our own controls and the page's styling, which the native API would
     hand over to the browser.

     THE HOST IS PROMOTED WHERE IT STANDS — no DOM move. Moving <product-model> would
     disconnect and reconnect a custom element that model-viewer, Shopify's product-model and
     every listener in this file are attached to. Verified on the live page that nothing in the
     ancestor chain sets transform/filter/perspective/will-change/contain, any of which would
     quietly turn `fixed` into `absolute`.

     THE BACK BUTTON CLOSES IT. A history entry is pushed on open, so Android's back gesture
     and iOS's swipe-back dismiss the overlay instead of leaving the product page — which is
     the failure that got Shopify's own fullscreen removed in session 10: "it strands the
     customer on a blank page away from the price and the buy button". */
  function addFullscreen(host, mv, state, controls) {
    const ICON_EXPAND =
      '<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/>';
    const ICON_CLOSE = '<path d="M6 6l12 12M18 6L6 18"/>';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sauna3d-fs-btn';
    btn.setAttribute('aria-label', 'View full screen');
    btn.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true">${ICON_EXPAND}</svg>`;

    let open = false;
    let pushed = false;

    const paint = () => {
      btn.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true">${
        open ? ICON_CLOSE : ICON_EXPAND
      }</svg>`;
      btn.setAttribute('aria-label', open ? 'Exit full screen' : 'View full screen');
    };

    /* Two frames before refitting. The first lands the new layout, the second lets
       model-viewer resize its own canvas to it — solving against a stage that has changed in
       CSS but not yet in the component gives a radius for the old size. */
    const refitSoon = () => {
      requestAnimationFrame(() => requestAnimationFrame(() => refitFraming(mv, host, state)));
    };

    const setOpen = (next, fromHistory) => {
      if (next === open) return;
      open = next;
      if (open) {
        host.setAttribute('data-sauna3d-fs', '');
        document.body.classList.add('sauna3d-fs-open');
        if (!fromHistory && window.history && window.history.pushState) {
          try {
            window.history.pushState({ sauna3dFs: true }, '');
            pushed = true;
          } catch (e) {
            pushed = false; // some embedded browsers throw; the button still closes it
          }
        }
      } else {
        host.removeAttribute('data-sauna3d-fs');
        document.body.classList.remove('sauna3d-fs-open');
        /* ⚠ CLEAR THE TOGGLES ON THE WAY OUT, or the customer is stranded.
           On touch the control row exists ONLY in full screen (see the CSS), so someone who
           turns dimensions on and then closes would land back in the gallery with the overlay
           still drawn over the sauna and no button anywhere to turn it off. Resetting runs
           each toggle's own handler, so the overlay clears and the front wall closes exactly
           as a second tap would.

           Matched to the CSS gate rather than the house touch rule, because it is that gate
           that decides whether the row is reachable. Desktop keeps its state across the
           button, which it should — the row never went anywhere there. */
        if (window.matchMedia('(hover: none) and (pointer: coarse)').matches && controls) {
          controls.reset();
        }
        /* Unwind our own history entry when the button closed it, so a later back press does
           not have to consume a state that no longer corresponds to anything on screen. */
        if (!fromHistory && pushed) {
          pushed = false;
          window.history.back();
        }
      }
      paint();
      refitSoon();
    };

    btn.addEventListener('click', (e) => {
      // The gallery is a scroll-snap carousel and model-viewer is listening too.
      e.preventDefault();
      e.stopPropagation();
      setOpen(!open);
    });

    window.addEventListener('popstate', () => {
      if (open) setOpen(false, true);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && open) setOpen(false);
    });

    /* Rotating the phone is the case that actually makes the model big — at MODEL_YAW the
       cabin is wider than it is tall, so the solve is width-bound and portrait full screen
       buys only 350 -> 390px of width, while landscape buys 844. */
    let t = null;
    window.addEventListener('resize', () => {
      if (!open) return;
      clearTimeout(t);
      t = setTimeout(() => refitFraming(mv, host, state), 120);
    });

    host.appendChild(btn);

    return {
      isOpen: () => open,
      close: () => setOpen(false),
    };
  }

  function addControls(host, mv, dims, reveal) {
    const bar = document.createElement('div');
    bar.className = 'sauna3d-controls';
    const toggles = [];

    /* ── THE PILL DOES NOT CHANGE WIDTH WHEN IT IS PRESSED ────────────────────────────
       Ryan: both labels swap on click, so each button used to resize under the thumb —
       "Show dimensions" is a little wider than "Hide dimensions" in a proportional face, and
       with two pills centred in a row, one changing width nudges the other sideways.

       Solved by carrying BOTH labels in the button at once and stacking them in a single
       grid cell: the visible one and a hidden twin holding the other state's text. A grid
       cell is as wide as its widest occupant, so the pill is permanently sized to the longer
       of the two and the swap moves nothing.

       Deliberately NOT a measured `min-width`. Any number we compute is a guess about a font
       that has not necessarily loaded yet — a width measured before the webfont swaps is
       measured in the fallback face — and it would be wrong again the moment a label is
       reworded or the type scale moves. The twin re-measures itself for free, in whatever
       font actually rendered.

       The twin is `aria-hidden`, so the accessible name stays the visible label alone. */
    const setLabel = (b, text) => {
      const t = b.querySelector('.sauna3d-btn__t');
      if (t) t.textContent = text;
      else b.textContent = text;
    };

    const button = (label, altLabel, onClick) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'sauna3d-btn';

      const t = document.createElement('span');
      t.className = 'sauna3d-btn__t';
      t.textContent = label;
      const ghost = document.createElement('span');
      ghost.className = 'sauna3d-btn__g';
      ghost.setAttribute('aria-hidden', 'true');
      ghost.textContent = altLabel;
      b.appendChild(t);
      b.appendChild(ghost);

      b.setAttribute('aria-pressed', 'false');
      b.addEventListener('click', (e) => {
        // The gallery is a scroll-snap carousel and model-viewer is listening too.
        e.preventDefault();
        e.stopPropagation();
        const next = b.getAttribute('aria-pressed') !== 'true';
        b.setAttribute('aria-pressed', String(next));
        b.classList.toggle('is-on', next);
        onClick(next, b);
      });
      bar.appendChild(b);
      toggles.push({ el: b, onClick: onClick });
      return b;
    };

    if (dims) {
      button('Show dimensions', 'Hide dimensions', (next, el) => {
        setLabel(el, next ? 'Hide dimensions' : 'Show dimensions');
        dims.toggle(next);
      });
    }

    // Only offer the interior if the clip is actually in this file — an older .glb rebuilt
    // before the reveal existed would otherwise get a button that does nothing.
    const addInterior = () => {
      if (!reveal.available()) return;
      if (bar.querySelector('[data-sauna3d-interior]')) return;
      const b = button('Show interior', 'Hide interior', (next, el) => {
        setLabel(el, next ? 'Hide interior' : 'Show interior');
        reveal.play(next);
      });
      b.dataset.sauna3dInterior = '1';
    };
    if (mv.loaded) addInterior();
    mv.addEventListener('load', addInterior, { once: true });

    host.appendChild(bar);

    return {
      /* Un-press anything the customer left on, running each button's own handler so the
         overlay clears and the front wall closes exactly as a second click would — the label
         and aria-pressed come back with it, because that is the same code path. */
      reset() {
        for (const t of toggles) {
          if (t.el.getAttribute('aria-pressed') !== 'true') continue;
          t.el.setAttribute('aria-pressed', 'false');
          t.el.classList.remove('is-on');
          t.onClick(false, t.el);
        }
      },
    };
  }

  // ── Wiring ─────────────────────────────────────────────────────────────────────────────
  // ProductModel.loadContent() appends the <model-viewer> synchronously, but Shopify's
  // ModelViewerUI arrives later over the network (assets/product-model.js:17-27), so the
  // element can exist before it is upgraded. Polling a couple of frames is cheaper and more
  // robust than racing that.
  /* Point this product-model at the touch build, BEFORE Shopify creates the <model-viewer>.
     Returns true if it swapped.

     ⚠ THE TEMPLATE, NOT THE ELEMENT. Shopify's <product-model> keeps its markup in a
     <template> and only stamps it out when loadContent() runs. Rewriting `mv.src` after the
     fact works, but the element has already begun fetching the DESKTOP file by then — so the
     phone downloads ~1.8 MB it will never render, on the connection least able to afford it.
     Swapping inside the template means the first and only fetch is the right one.

     Falls back to the live element if there is no template (a re-init, or a future theme that
     stamps eagerly), because a second download is still better than the wrong model. */
  function useMobileBuild(host) {
    if (!MOBILE_SRC || !isTouchLayout()) return false;

    const tpl = host.querySelector('template');
    const inTpl = tpl && tpl.content && tpl.content.querySelector('model-viewer');
    if (inTpl) {
      inTpl.setAttribute('src', MOBILE_SRC);
      return true;
    }

    const live = host.querySelector('model-viewer');
    if (live) {
      live.setAttribute('src', MOBILE_SRC);
      return true;
    }
    return false;
  }

  function whenModelViewer(host, cb) {
    let tries = 0;
    const look = () => {
      const mv = host.querySelector('model-viewer');
      if (mv) return cb(mv);
      if (++tries > 120) return; // ~2s at 60fps, then give up quietly
      requestAnimationFrame(look);
    };
    look();
  }

  function setupModel(host, ext) {
    whenModelViewer(host, (mv) => {
      applyCameraSettings(mv);

      const state = { home: 0 };
      attachFloorClamp(mv, state);
      attachZoom(mv, state);

      const reveal = makeReveal(mv);
      const dims = ext ? makeDims(mv, host, ext, state) : null;
      const controls = addControls(host, mv, dims, reveal);
      /* After the controls, so the row exists for the expanded layout to re-centre, and so
         the button is appended last and sits above it in paint order. Safe before the model
         has loaded: refitFraming no-ops until the load solve has populated state.box. */
      addFullscreen(host, mv, state, controls);
      keepModelLive(host);
      attachCursors(mv);
      attachBusyFade(host, mv);
      attachSlideReset(host, mv, state, controls);

      /* The orbit is a goal again the moment the model loads and `auto` resolves, so re-land it
         on every load rather than only at setup. Cheap, and it closes the window between the
         model becoming renderable and onReady running. */
      mv.addEventListener('load', () => {
        if (typeof mv.jumpCameraToGoal === 'function') mv.jumpCameraToGoal();
      });

      const onReady = () => {
        boundingCenter(mv); // capture before anything can move the target
        // Solve the closest distance that still leaves LOAD_MARGIN of white on all four sides.
        // Only possible once there is a model: its size, the resolved field of view and the
        // frame's own aspect are all needed.
        const o = mv.getCameraOrbit();
        const d = mv.getDimensions();
        const fov = (mv.getFieldOfView() * Math.PI) / 180;
        const s = host.getBoundingClientRect();
        if (o && o.radius && d && fov > 0 && s.width && s.height) {
          /* Solved AT THE ANGLE THE CAMERA IS ABOUT TO BE AT, not at whatever `auto` left it
             on — the framing depends on the orbit now that it is a margin around a projected
             silhouette rather than a height ratio. */
          const goal = {
            theta: (HOME_AZ * Math.PI) / 180,
            phi: (86 * Math.PI) / 180,
            radius: o.radius,
          };
          // The eight corners of the carcass, in model space and then turned into world space
          // through MODEL_YAW — the same transform the dimension lines use.
          const hx = ext ? (ext.w / 2 + MESH_OVERSAIL_IN) * IN_TO_M : d.x / 2;
          const hz = ext ? (ext.d / 2 + MESH_OVERSAIL_IN) * IN_TO_M : d.z / 2;
          const yr = (MODEL_YAW * Math.PI) / 180;
          const yc = Math.cos(yr);
          const ys = Math.sin(yr);
          const corners = [];
          for (const sx of [-1, 1]) {
            for (const sz of [-1, 1]) {
              for (const y of [0, d.y]) {
                const x = sx * hx;
                const z = sz * hz;
                corners.push({ x: x * yc + z * ys, y, z: -x * ys + z * yc });
              }
            }
          }
          // Floored at the cabin's half-diagonal so the solve can never put the camera inside
          // the carcass on some future model with unusual proportions.
          const floor = Math.hypot(d.x, d.z) / 2 + 0.2;
          /* ⚠ THE PIVOT IS SET FROM THE GEOMETRY, not inherited from model-viewer.
             Left alone, `auto` targets the MESH bounding-box centre — and the mesh is not
             symmetric about the cabin: the door handle stands proud on one side and the roof
             cap oversails. Measured, that put the pivot 0.51" off the cabin's axis on both
             saunas. Small, and it is exactly the kind of small that differs per model, which
             is the thing worth removing: every sauna should turn about the same point relative
             to itself, whatever its mesh happens to stick out.

             The assembler builds every model centred on the origin with its floor at y=0, and
             MODEL_YAW is a rotation about Y through that origin, so the cabin's axis is x=0,
             z=0 at any yaw. Height comes from the manual, like every other figure a customer
             could measure. */
          const pivotY = (ext ? ext.h * IN_TO_M : d.y) / 2;
          const t = { x: 0, y: pivotY, z: 0 };
          mv.cameraTarget = `0m ${pivotY}m 0m`;

          const margin = LOAD_MARGIN * Math.min(s.width, s.height);
          const solved = solveRadius(corners, goal, t, mv.getFieldOfView(), s.width, s.height, margin, floor);
          /* Divided, not multiplied: a LARGER model is a CLOSER camera, so 5% bigger is 5%
             less radius. The floor is applied after, so the zoom can never win against the
             guard that keeps the camera outside the carcass. */
          state.home = Math.max(solved / (narrowStage() ? NARROW_LOAD_ZOOM : 1), floor);
          /* ⚠ THE TARGET IS THE PIVOT, so it stays on the cabin's own centre and the load
             framing solves DISTANCE ONLY.

             An earlier pass panned it to centre the silhouette in the frame, which is a real
             improvement to the composition and cost something worth more: the target is the
             point the model orbits, so a drag spun the cabin about a spot ~6" off its middle.
             That is plainly visible on a 61"-wide sauna and it is the first thing anyone does
             after the page loads. Frame-centring is a nicety; a pivot that sits where the
             customer expects it is not.

             The composition is therefore slightly left of centre, because the camera is 14deg
             off square and the cabin is deeper than it is wide. That is the honest trade and it
             is the right way round. */
          state.homeTarget = { x: t.x, y: t.y, z: t.z };
          /* The cabin's own box in MODEL space, for the aim ray and the wall guard. Not the
             world AABB: at MODEL_YAW that is the rotated envelope and measures 2.38m across a
             1.57m-wide sauna. */
          state.box = {
            lo: { x: -hx, y: 0, z: -hz },
            hi: { x: hx, y: d.y, z: hz },
          };
          /* The resolved `auto` field of view, kept because the dimension fit has to solve at
             the angle the camera will be at AFTER `fieldOfView = 'auto'` lands — not at
             whatever a mid-pinch camera reports when the button is pressed. */
          state.homeFov = (fov * 180) / Math.PI;
          applyRadiusLimits(mv, state);
          mv.cameraOrbit = `${HOME_AZ}deg 86deg ${state.home}m`;
          // The write above is a GOAL the component glides to. jumpCameraToGoal lands it
          // immediately, which is what a load framing has to do — a page that opens by
          // sliding the camera inwards reads as a bug, not as a flourish.
          if (typeof mv.jumpCameraToGoal === 'function') mv.jumpCameraToGoal();
          diag.framing = { auto: o.radius, solved, floor, margin, home: state.home, fovDeg: (fov * 180) / Math.PI };
        } else if (o && o.radius) {
          state.home = o.radius;
          applyRadiusLimits(mv, state);
          diag.framing = { auto: o.radius, skipped: true, d: !!d, fov };
        }
        /* REVEAL ONLY ONCE THE FRAMED FRAME HAS ACTUALLY BEEN PAINTED.
           `load` means the file is parsed, not that anything correct is on screen yet: the
           framing write above is a goal, jumpCameraToGoal lands it during the NEXT frame, and
           model-viewer's environment and ground shadow settle a frame after that — which is
           the "washed out, then it gets colour" part of the sequence. Starting the fade on
           `load` uncovers the canvas mid-settle and puts all of that back on screen, just
           faster. Two frames is the difference between revealing the model and revealing the
           model loading. */
        /* The environment goes on FIRST and the reveal waits for it, so the customer never sees
           the cabin lit by model-viewer's default and then re-lit a beat later. Everything
           below happens under the poster. */
        applyEnvironment(mv, () => {
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              host.setAttribute('data-sauna3d-fading', '');
              setTimeout(() => {
                host.removeAttribute('data-sauna3d-pending');
                removeLoader(host); // stop animating a ring nobody can see
              }, POSTER_FADE_MS);
            })
          );
        });
      };

      if (mv.loaded) onReady();
      else mv.addEventListener('load', onReady, { once: true });
    });
  }

  function initGallery(gallery) {
    // The hoisted slide is the one carrying .is-current-variant — media-gallery.liquid points
    // featured_media at the model when it hoists, so exactly one item has it.
    const item = gallery.querySelector('.media-viewer__item.is-current-variant');
    if (!item) return;
    const host = item.querySelector('product-model');
    if (!host || host.dataset.sauna3dInit) return;
    host.dataset.sauna3dInit = '1';
    host.setAttribute('data-sauna3d-hoisted', '');

    let ext = null;
    const cfg = gallery.parentElement && gallery.parentElement.querySelector('.js-sauna3d-dims');
    const cfgEl = cfg || document.querySelector('.js-sauna3d-dims');
    if (cfgEl) {
      try {
        const raw = JSON.parse(cfgEl.textContent);
        const v = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (v && v.w && v.d && v.h) ext = v;
      } catch (e) {
        /* no metafield, no overlay — the button simply does not appear */
      }
    }

    /* ONE LOAD PATH, TOUCH AND DESKTOP ALIKE. Activate it for them: Baymard measured 83% of
       users never trying an available 360-view, so anything gated behind a tap is a feature
       most customers never see.

       ⚠ Touch used to take a separate branch here — a "View in 3D" pill over the product
       photograph, loading only on tap, to spare a phone ~1.8 MB it might not want. Removed
       2026-08-05: it made the phone the odd one out, and it was three pieces of load furniture
       (photograph, centre glyph, pill) in front of the thing the customer came to the slide
       for. The data argument it rested on is weaker than it looks — the fetch is already
       deferred to window.load + idle, so it never competes with the LCP image on either
       device, and a phone that reaches a product page has committed to far more than one
       gallery slide.

       What the touch path keeps is the CSS below it: `touch-action: pan-y` on the model, so
       vertical page scroll survives, and the 45px controls. Horizontal swipe on THIS slide is
       spent on spin — the sauna templates ship media_arrows and media_thumbs "always", so
       there are two other ways off the slide on screen at all times.

       window.load + idle, never DOMContentLoaded: 1.8 MB must not compete with the LCP image.
       loadContent(focus=false, pause=false): focus=false so page load does not yank focus into
       the canvas, pause=false so it does not run pauseAllMedia() for no reason. */
    host.setAttribute('data-sauna3d-pending', '');
    addLoader(host);

    const activate = () => {
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
      idle(() => {
        // BEFORE loadContent, or the phone fetches the desktop model first. See useMobileBuild.
        useMobileBuild(host);
        if (typeof host.loadContent === 'function') host.loadContent(false, false, 'auto');
        setupModel(host, ext);
      });
    };

    if (document.readyState === 'complete') activate();
    else window.addEventListener('load', activate, { once: true });
  }

  /* ── 50ms TAP HAPTICS ON OUR CONTROLS ─────────────────────────────────────────────────
     Ryan, 2026-08-04. Same buzz the header controls have had since 2026-07-11 — see
     assets/custom-header-haptics.js, which is the canonical statement of the house rule and
     where the 50ms lives. This mirrors it rather than extending it: that file is named for
     the header, loads on every page on the site, and its selector list is a list of header
     things. Sauna controls are neither, and this file only exists on a sauna PDP.

     ⚠ IF THE HOUSE DURATION EVER CHANGES, it has to change in both. That is the cost of the
     split and it is the reason this comment names the other file explicitly.

     Copied from it exactly, because each part is load-bearing:
       - `'vibrate' in navigator` guard. iOS Safari has no Vibration API at all, so this is a
         silent no-op there and Android is where it actually buzzes. Without the guard it
         throws on every tap on an iPhone.
       - Delegated on `document` in the CAPTURE phase. Our buttons call stopPropagation() —
         the gallery is a scroll-snap carousel and model-viewer is listening too — so a
         bubble-phase listener would never see them. Capture runs first and is immune.
       - `click`, not `pointerdown`, so starting a drag on the model does not misfire a buzz.

     ONE DELIBERATE DEVIATION: the gate is `(hover: none) and (pointer: coarse)` where the
     header file uses `(max-width: 768px) and (pointer: coarse)`. Both exclude Ryan's
     touchscreen laptop, which is what the house rule is protecting against — but a phone
     turned sideways is 844px wide and would fail the width test, so the width form would go
     silent in landscape while the buttons it is attached to are still on screen. Same gate
     the full-screen button itself uses, for the same reason. */
  function attachHaptics() {
    if (!('vibrate' in navigator)) return;
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
    document.addEventListener(
      'click',
      (e) => {
        if (!mq.matches) return;
        if (!e.target.closest('.sauna3d-btn, .sauna3d-fs-btn')) return;
        navigator.vibrate(50);
      },
      true
    );
  }

  function initAll() {
    document.querySelectorAll('media-gallery').forEach(initGallery);
    /* Once per page, not once per gallery — the listener is delegated on document, so a
       second registration would buzz twice on a page with two galleries. */
    if (!document.documentElement.dataset.sauna3dHaptics) {
      document.documentElement.dataset.sauna3dHaptics = '1';
      attachHaptics();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* The theme editor re-renders sections and media-gallery.js re-inits after 200ms in
     designMode (assets/media-gallery.js:6-10). Re-run so the hoisted slide survives. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => setTimeout(initAll, 250));
  }
})();
/* END CUSTOM_SAUNA3D_BY_CLAUDE */
